import { useRouter } from "next/router";
import {
  Card,
  Stack,
  TextStyle,
  Select,
  Spinner,
  EmptyState,
  Toast,
  Link,
} from "@shopify/polaris";
import CodeEditor from "./code-editor";

import { useCallback, useEffect, useState } from "react";
import { useAxios } from "../../hooks/useAxios";
import { updateCustomWidget } from "../../utils/queries";
import UpgradeCardSection from "../upgrade-card-section-large";
import { handleAdminError } from "../../utils/helpers";

function CustomWidget({
  shopOrigin,
  scopes,
  hasFeature,
  getMinPlanForFeature,
  ...props
}) {
  const router = useRouter();
  const [axios] = useAxios();

  const [isLoading, setisLoading] = useState(false);
  const [loadTheme, setloadTheme] = useState(false);
  const [customWidgets, setcustomWidgets] = useState([]);
  const [customWidget, setcustomWidget] = useState({});
  const [customWidgetJs, setcustomWidgetJs] = useState("");
  const [customWidgetCss, setcustomWidgetCss] = useState("");
  const [customWidgetLiquid, setcustomWidgetLiquid] = useState("");
  const [allThemes, setallThemes] = useState([]);
  const [publishedTheme, setpublishedTheme] = useState({});
  const [isEdit, setisEdit] = useState(false);
  const [selectedThemeId, setselectedThemeId] = useState("");
  const [deafultCode, setdeafultCode] = useState("");
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    fetchWidgetLocalData();
  }, [selectedThemeId]);

  useEffect(() => {
    let forCurrentTheme = customWidgets.find(
      (c) => c.theme_id === Number(selectedThemeId)
    );

    let theme = allThemes.find((t) => t.id === Number(selectedThemeId));
    setpublishedTheme(theme);
    if (forCurrentTheme) {
      setcustomWidget(forCurrentTheme);
    } else {
      let data = {
        code: deafultCode,
        active: 0,
      };
      setcustomWidget(data);
    }
  }, [selectedThemeId, deafultCode, customWidgets]);

  useEffect(() => {
    loadData();
  }, []);

  const handleStatusChange = async (val) => {
    setisLoading(true);
    let errorOccured = false;
    try {
      if (val) {
        const resp = await axios.post(
          `${process.env.HOST}/script_tags/add_loop_snippet_custom`,
          {
            shop: shopOrigin,
            theme: publishedTheme,
            snippetVersion: props.snippet_version,
            liquidCode: customWidgetLiquid,
            jsCode: customWidgetJs,
            cssCode: customWidgetCss,
            active: val,
          }
        );

        if (resp?.data?.error) {
          errorOccured = true;
        }
      } else {
        const resp = await axios.post(
          `${process.env.HOST}/script_tags/add_loop_snippet`,
          {
            shop: shopOrigin,
            theme: publishedTheme,
            snippetVersion: props.snippet_version,
            active: val,
          }
        );
      }

      if (!errorOccured) {
        let res = await updateCustomWidget(axios, {
          theme_id: selectedThemeId,
          code: customWidget?.code || deafultCode,
          active: val,
        });
        setIsActive(val ? true : false);
      }
    } catch (error) {
      handleAdminError(
        error,
        shopOrigin,
        "handleStatusChange",
        "CustomWidget",
        "",
        {
          payload: {
            shop: shopOrigin,
            theme: publishedTheme,
            snippetVersion: props.snippet_version,
            code: customWidget?.code || deafultCode,
            active: val,
          },
        }
      );
    }
    await fetchWidgetLocalData();
    props.refreshedData();
    setisLoading(false);
  };

  const [successActive, setsuccessActive] = useState(false);
  const toggleSuccessToast = useCallback(
    () => setsuccessActive((successActive) => !successActive),
    []
  );

  const [errorActive, seterrorActive] = useState(false);
  const toggleErrorToast = useCallback(
    () => seterrorActive((errorActive) => !errorActive),
    []
  );

  const toastSuccessMarkup = successActive ? (
    <Toast content="File saved successfully." onDismiss={toggleSuccessToast} />
  ) : null;

  const toastErrorMarkup = errorActive ? (
    <Toast
      content="An error occured while saving, please check the code."
      error={true}
      onDismiss={toggleErrorToast}
    />
  ) : null;

  const changeInCode = async (val, type) => {
    setisLoading(true);
    try {
      const resp = await axios.post(
        `${process.env.HOST}/script_tags/add_loop_snippet_custom`,
        {
          shop: shopOrigin,
          theme: publishedTheme,
          snippetVersion: props.snippet_version,
          liquidCode: customWidgetLiquid,
          jsCode: customWidgetJs,
          cssCode: customWidgetCss,
          active: val, //on code change we are sendind the updated code as value
        }
      );

      if (!resp?.data?.error) {
        toggleSuccessToast();
      } else {
        toggleErrorToast();
      }
    } catch (error) {
      handleAdminError(error, shopOrigin, "changeInCode", "CustomWidget", "", {
        payload: {
          shop: shopOrigin,
          theme: publishedTheme,
          snippetVersion: props.snippet_version,
          code: val,
          active: isActive,
        },
      });
    }
    setisLoading(false);
  };

  const loadData = async () => {
    setisLoading(true);
    const { data } = await axios.post(
      `${process.env.HOST}/script_tags/get_themes`,
      {
        shop: shopOrigin,
      }
    );

    if (data?.themes) {
      let themes = data?.themes.map((t) => {
        return {
          ...t,
          value: t.id.toString(),
          label: `${t.name} ${t.published ? "(Published)" : ""}`,
        };
      });
      setallThemes(themes || []);
      let pubhishedTheme = themes.find((t) => t.published);
      setselectedThemeId(pubhishedTheme.id.toString());
    }

    setisLoading(false);
  };

  const fetchWidgetLocalData = async () => {
    setisLoading(true);
    if (!(selectedThemeId && shopOrigin)) {
      return;
    }

    const responseCustom = await axios.post(
      `${process.env.HOST}/install/getCustomWidgetFilesFromLocal`,
      {
        shop: shopOrigin,
        theme: selectedThemeId,
      }
    );

    setcustomWidgetLiquid(responseCustom.data.loopSnippetStandard);
    setcustomWidgetJs(responseCustom.data.loopSnippetStandardJS);
    setcustomWidgetCss(responseCustom.data.loopSnippetStandardCSS);
    setIsActive(responseCustom.data.isActive);
    setisLoading(false);
  };

  if (isLoading || !(customWidgetLiquid || customWidgetJs || customWidgetCss)) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "200px",
        }}
      >
        <Spinner accessibilityLabel="Spinner example" size="large" />
      </div>
    );
  }

  if (
    customWidgetLiquid ||
    customWidgetJs ||
    customWidgetCss ||
    (customWidgets && customWidgets.length) ||
    isEdit
  ) {
    return (
      <div>
        {toastErrorMarkup}
        {toastSuccessMarkup}
        <TextStyle variation="subdued">
          Use this feature to enable custom widget flow for your store. Once
          activated, standard widget settings will not be applicable on the
          selected theme(s).{" "}
          <Link
            external={true}
            url="https://learn.loopwork.co/en/article/custom-subscription-widget-80rzzg/"
          >
            Learn more
          </Link>
        </TextStyle>
        <div style={{ margin: "10px 0" }}>
          <Stack alignment="center">
            <TextStyle variation="strong">Theme </TextStyle>
            <Select
              disabled={!hasFeature("custom_widget")}
              options={allThemes}
              onChange={async (val) => {
                setloadTheme(true);
                setselectedThemeId(val);
                await new Promise((resolve) => setTimeout(resolve, 100));
                setloadTheme(false);
              }}
              value={selectedThemeId}
            />
          </Stack>
        </div>
        {!loadTheme ? (
          <CodeEditor
            disabled={!hasFeature("custom_widget")}
            themeId={selectedThemeId}
            customWidget={customWidget}
            customWidgetLiquid={customWidgetLiquid}
            customWidgetJs={customWidgetJs}
            customWidgetCss={customWidgetCss}
            setcustomWidgetLiquid={(val) => setcustomWidgetLiquid(val)}
            setcustomWidgetJs={(val) => setcustomWidgetJs(val)}
            setcustomWidgetCss={(val) => setcustomWidgetCss(val)}
            shopOrigin={shopOrigin}
            isLoading={isLoading}
            isActive={isActive}
            height="200px"
            unsavedValue={(val, id) => {
              setcustomWidget({
                ...customWidget,
                unsavedCode: val,
              });
            }}
            changeInCode={(val) => {
              changeInCode(val);
            }}
            handleStatusChange={(val) => {
              handleStatusChange(val);
            }}
          />
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "200px",
            }}
          >
            <Spinner size="large" />
          </div>
        )}
      </div>
    );
  }

  return (
    <Card>
      {!hasFeature("custom_widget") ? (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <UpgradeCardSection plan={getMinPlanForFeature("custom_widget")} />
        </div>
      ) : null}

      <EmptyState
        heading="No custom widget created yet"
        action={{
          content: "Create now",
          disabled: !hasFeature("custom_widget"),
          onAction: () => {
            setisEdit(true);
          },
        }}
        image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
      ></EmptyState>
    </Card>
  );
}
export default CustomWidget;
